import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { LuppApi } from '../lupp.service';
import { LUPP_API_BASE_PATH, LUPP_QUERY_KEYS } from '../constants.lupp';
import {
  BookingSearchRequest,
  SalesConsultantsCalendarResponse,
  TestDriveAvailableSlotsResponse,
  TestDriveBookingsResponse,
  TestDriveVehiclesResponse,
  UseGetSalesConsultantsCalendarParams,
  UseGetTestDriveAvailableSlotsParams,
  UseGetTestDriveVehiclesParams,
  UseSearchBookingsParams,
} from './dto.test-drive';
import { AgencyUserAvailabilityResponse } from '../prospect/dto.test-drive-schedule';

// Required by the test-drive endpoints; the shared interceptor only sets it for FileHub.
const TENANT_HEADER = { headers: { 'X-Tenant-Id': 'lucid-partner-us' } };

/**
 * Query parameter x-sales forwards to test-drive-manager (TestDriveLocationInterceptor) to scope
 * a read to one business partner location.
 */
const LOCATION_QUERY_PARAM = 'locationId';

/** Adds the location to the query when one is given; left off so the backend default applies. */
const withLocation = (query: URLSearchParams, locationId?: string): URLSearchParams => {
  if (locationId) query.set(LOCATION_QUERY_PARAM, locationId);
  return query;
};

/*
|--------------------------------------------------------------------------
| GET TEST DRIVE VEHICLES
|--------------------------------------------------------------------------
*/

/**
 * Hook to fetch the test-drive vehicles configured for an agency
 *
 * @param params Agency id, filter and query options
 * @returns Query result with the agency's test-drive vehicles
 *
 * @example
 * ```typescript
 * const { data: vehicles } = useGetTestDriveVehicles({
 *   agencyId: userIdentity?.businessOrganizationId ?? '',
 *   availableOnly: true,
 * });
 * ```
 */
export const useGetTestDriveVehicles = (params: UseGetTestDriveVehiclesParams) => {
  const { agencyId, locationId, availableOnly = true, enabled = true } = params;

  return useQuery({
    queryKey: [LUPP_QUERY_KEYS.TEST_DRIVE_VEHICLES, agencyId, locationId, availableOnly],
    queryFn: async () => {
      const query = withLocation(
        new URLSearchParams({ availableOnly: String(availableOnly) }),
        locationId
      );
      const response = await LuppApi.get<TestDriveVehiclesResponse>(
        `${LUPP_API_BASE_PATH}/agency/${agencyId}/test-drive-vehicles?${query}`,
        { axiosConfig: TENANT_HEADER }
      );
      return response;
    },
    enabled: enabled && Boolean(agencyId),
  });
};

/*
|--------------------------------------------------------------------------
| GET TEST DRIVE AVAILABLE SLOTS
|--------------------------------------------------------------------------
*/

/**
 * Hook to fetch bookable test-drive slots for a model/trim on a given day
 *
 * @param params Agency id, model, trim, selected date and query options
 * @returns Query result with the available slots
 *
 * @example
 * ```typescript
 * const { data: slots } = useGetTestDriveAvailableSlots({
 *   agencyId: userIdentity?.businessOrganizationId ?? '',
 *   model: 'Air',
 *   trim: 'Touring',
 *   selectedDate: '2026-09-12',
 * });
 * ```
 */
export const useGetTestDriveAvailableSlots = (params: UseGetTestDriveAvailableSlotsParams) => {
  const {
    agencyId,
    locationId,
    model,
    trim,
    selectedDate,
    testDriveType,
    returnDate,
    excludeBookingId,
    enabled = true,
  } = params;

  return useQuery({
    queryKey: [
      LUPP_QUERY_KEYS.TEST_DRIVE_AVAILABLE_SLOTS,
      agencyId,
      locationId,
      model,
      trim,
      selectedDate,
      testDriveType,
      returnDate,
      excludeBookingId,
    ],
    queryFn: async () => {
      const query = withLocation(new URLSearchParams({ model, selectedDate }), locationId);
      if (trim) query.set('trim', trim);
      if (testDriveType) query.set('testDriveType', testDriveType);
      if (returnDate) query.set('returnDate', returnDate);
      if (excludeBookingId) query.set('excludeBookingId', excludeBookingId);

      const response = await LuppApi.get<TestDriveAvailableSlotsResponse>(
        `${LUPP_API_BASE_PATH}/agencies/${agencyId}/available-slots?${query}`,
        { axiosConfig: TENANT_HEADER }
      );
      return response;
    },
    enabled: enabled && Boolean(agencyId) && Boolean(model) && Boolean(selectedDate),
  });
};

/*
|--------------------------------------------------------------------------
| GET AGENCY USER AVAILABILITY
|--------------------------------------------------------------------------
*/

/**
 * Hook to fetch agency users available to be assigned as the host for a test drive,
 * optionally scoped to a location.
 * Calls x-sales's `GET /v1/agency/{agencyId}/users/availability?locationId=...` (UserController).
 */
export const useGetAgencyUserAvailability = (params: {
  agencyId: string;
  locationId?: string;
  enabled?: boolean;
}) => {
  const { agencyId, locationId, enabled = true } = params;

  return useQuery<AgencyUserAvailabilityResponse>({
    queryKey: [LUPP_QUERY_KEYS.AGENCY_USER_AVAILABILITY, agencyId, locationId],
    queryFn: async () => {
      const query = withLocation(new URLSearchParams(), locationId).toString();
      const response = await LuppApi.get<AgencyUserAvailabilityResponse>(
        `${LUPP_API_BASE_PATH}/agency/${agencyId}/users/availability${query ? `?${query}` : ''}`,
        { axiosConfig: TENANT_HEADER }
      );
      return response;
    },
    enabled: enabled && Boolean(agencyId),
  });
};

/*
|--------------------------------------------------------------------------
| GET SALES CONSULTANTS CALENDAR
|--------------------------------------------------------------------------
*/

/**
 * Hook to fetch one day of the sales consultants calendar (the scheduler board data). Pass
 * `locationId` so consultants, availability and bookings come from that location only.
 *
 * @example
 * ```typescript
 * const { data } = useGetSalesConsultantsCalendar({ agencyId, locationId, date: '2026-09-12' });
 * ```
 */
export const useGetSalesConsultantsCalendar = (params: UseGetSalesConsultantsCalendarParams) => {
  const { agencyId, locationId, date, enabled = true } = params;

  return useQuery({
    queryKey: [LUPP_QUERY_KEYS.SALES_CONSULTANTS_CALENDAR, agencyId, locationId, date],
    queryFn: async () => {
      const query = withLocation(
        new URLSearchParams({ startDate: date, endDate: date }),
        locationId
      );
      const response = await LuppApi.get<SalesConsultantsCalendarResponse>(
        `${LUPP_API_BASE_PATH}/scheduler/agency/${agencyId}/sales-consultants/calendar?${query}`,
        { axiosConfig: TENANT_HEADER }
      );
      return response;
    },
    enabled: enabled && Boolean(agencyId) && Boolean(date),
  });
};

/*
|--------------------------------------------------------------------------
| SEARCH BOOKINGS
|--------------------------------------------------------------------------
*/

/**
 * Hook to search test-drive bookings for the cross-prospect agency management view.
 * Calls x-sales's `POST /v1/test-drives/bookings/searches` (BookingController). The agency
 * scope is inferred server-side from the `X-Org-Id` header; all filter fields are optional.
 *
 * @example
 * ```typescript
 * const { data } = useSearchBookings({ status: 'Scheduled', pageNumber: 0, pageSize: 10 });
 * ```
 */
export const useSearchBookings = (params: UseSearchBookingsParams = {}) => {
  const {
    prospectId,
    status,
    vehicleModel,
    vin,
    appointmentStartDate,
    appointmentEndDate,
    searchText,
    testDriveId,
    testDriveType,
    locationName,
    appointmentEndFrom,
    appointmentEndTo,
    pageNumber,
    pageSize,
    sortBy,
    sortDirection,
    enabled = true,
  } = params;

  return useQuery({
    queryKey: [
      LUPP_QUERY_KEYS.TEST_DRIVE_BOOKINGS_SEARCH,
      prospectId,
      status,
      vehicleModel,
      vin,
      appointmentStartDate,
      appointmentEndDate,
      searchText,
      testDriveId,
      testDriveType,
      locationName,
      appointmentEndFrom,
      appointmentEndTo,
      pageNumber,
      pageSize,
      sortBy,
      sortDirection,
    ],
    queryFn: async () => {
      const request: BookingSearchRequest = {
        prospectId,
        status,
        vehicleModel,
        vin,
        appointmentStartDate,
        appointmentEndDate,
        searchText,
        testDriveId,
        testDriveType,
        locationName,
        appointmentEndFrom,
        appointmentEndTo,
        pageNumber,
        pageSize,
        sortBy,
        sortDirection,
      };
      const response = await LuppApi.post<BookingSearchRequest, TestDriveBookingsResponse>(
        `${LUPP_API_BASE_PATH}/test-drives/bookings/searches`,
        request,
        { axiosConfig: TENANT_HEADER }
      );
      return response;
    },
    enabled,
    // Keeps the grid (and the search box) mounted while a new search/sort/page is fetching.
    placeholderData: keepPreviousData,
  });
};
